import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  FleetReportApiResponse,
  FleetReportsApiResponse,
  FleetReportResponse,
  savedFleetReport,
  getPastFleetReports,
  updateFleetReportNameById,
  UpdateFleetReportNameResponse,
  getSpecificUserFleetReports,
  deleteFleetReportById,
  updateFleetReportData,
  UpdateFleetReportDataResponse,
  FleetUtilityDetails,
  FleetEnergyCosts,
} from "./fleetReportAction";

interface FleetReportState {
  report: FleetReportResponse | null;
  reports: FleetReportResponse[];
  loading: boolean;
  error: string | null;
  totalReports: number;
  currentPage: number;
  totalPages: number;
  deleteLoading: boolean;
  deleteError: string | null;
  energyCosts?: FleetEnergyCosts;
  utilityDetails?: FleetUtilityDetails;
  disableUtilityEnergyInfo?: boolean;
}

const initialState: FleetReportState = {
  report: null,
  reports: [],
  loading: false,
  error: null,
  totalReports: 0,
  currentPage: 1,
  totalPages: 1,
  deleteLoading: false,
  deleteError: null,
  energyCosts: undefined,
  utilityDetails: undefined,
  disableUtilityEnergyInfo: false,
};

const fleetReportSlice = createSlice({
  name: "fleetReport",
  initialState,
  reducers: {
    resetFleetReportState: (state) => {
      state.report = null;
      state.reports = [];
      state.error = null;
      state.loading = false;
      state.totalReports = 0;
      state.currentPage = 1;
      state.totalPages = 1;
      state.deleteLoading = false;
      state.deleteError = null;
    },
    clearFleetReportError: (state) => {
      state.error = null;
      state.deleteError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle savedFleetReport
      .addCase(savedFleetReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        savedFleetReport.fulfilled,
        (state, action: PayloadAction<FleetReportApiResponse>) => {
          state.loading = false;
          state.report = action.payload.data?.report || null;
          state.error = null;
        }
      )
      .addCase(
        savedFleetReport.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to submit fleet report";
        }
      )
      // Handle getPastFleetReports
      .addCase(getPastFleetReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getPastFleetReports.fulfilled,
        (state, action: PayloadAction<FleetReportsApiResponse>) => {
          state.loading = false;
          state.reports = action.payload.data?.reports || [];
          state.totalReports = action.payload.data?.total || 0;
          state.currentPage = action.payload.data?.page || 1;
          state.totalPages = action.payload.data?.pages || 1;
          state.error = null;
        }
      )
      .addCase(
        getPastFleetReports.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to retrieve fleet reports";
        }
      )
      // Handle getSpecificUserFleetReports
      .addCase(getSpecificUserFleetReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        getSpecificUserFleetReports.fulfilled,
        (state, action: PayloadAction<FleetReportsApiResponse>) => {
          state.loading = false;
          state.reports = action.payload.data?.reports || [];
          state.totalReports = action.payload.data?.total || 0;
          state.currentPage = action.payload.data?.page || 1;
          state.totalPages = action.payload.data?.pages || 1;
          state.error = null;
        }
      )
      .addCase(
        getSpecificUserFleetReports.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to retrieve fleet reports";
        }
      )
      // Handle updateFleetReportNameById
      .addCase(updateFleetReportNameById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateFleetReportNameById.fulfilled,
        (state, action: PayloadAction<UpdateFleetReportNameResponse>) => {
          state.loading = false;
          const updated = action.payload.data?.report;
          if (updated) {
            const index = state.reports.findIndex((r) => r._id === updated._id);
            if (index !== -1) {
              state.reports[index] = updated;
            }
            if (state.report && state.report._id === updated._id) {
              state.report = updated;
            }
          }
          state.error = null;
        }
      )
      .addCase(
        updateFleetReportNameById.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update fleet report name";
        }
      )
      // Handle deleteFleetReportById
      .addCase(deleteFleetReportById.pending, (state) => {
        state.deleteLoading = true;
        state.deleteError = null;
      })
      .addCase(
        deleteFleetReportById.fulfilled,
        (
          state,
          action: PayloadAction<{
            success: boolean;
            message: string;
            data?: { id: string };
          }>
        ) => {
          state.deleteLoading = false;
          const deletedId = action.payload.data?.id;
          if (deletedId) {
            state.reports = state.reports.filter((r) => r._id !== deletedId);
            state.totalReports = Math.max(0, state.totalReports - 1);
            if (state.report && state.report._id === deletedId) {
              state.report = null;
            }
          }
          state.deleteError = null;
        }
      )
      .addCase(
        deleteFleetReportById.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.deleteLoading = false;
          state.deleteError = action.payload || "Failed to delete fleet report";
        }
      )
      .addCase(updateFleetReportData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updateFleetReportData.fulfilled,
        (state, action: PayloadAction<UpdateFleetReportDataResponse>) => {
          state.loading = false;
          const updated = action.payload.data?.report;
          if (updated) {
            const index = state.reports.findIndex((r) => r._id === updated._id);
            if (index !== -1) {
              state.reports[index] = updated;
            }
            if (state.report && state.report._id === updated._id) {
              state.report = updated;
            }
          }
          state.error = null;
        }
      )
      .addCase(
        updateFleetReportData.rejected,
        (state, action: PayloadAction<string | undefined>) => {
          state.loading = false;
          state.error = action.payload || "Failed to update fleet report data";
        }
      );
  },
});

export const { resetFleetReportState, clearFleetReportError } =
  fleetReportSlice.actions;
export default fleetReportSlice.reducer;
